<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Orchestrate your flow"
        storageKey="welcome-message-process"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            Processes are the engine of your domain, executing the work needed
            to deliver your products and services. You can model them visually
            using
            <a
                href="https://stekz.com/ai-at-work-visualizing-and-streamlining-your-business-processes/"
                target="_blank"
                >BPMN</a
            >
            right here.
        </p>
        <p v-if="!process?.process_definition">
            Map out your first process to start orchestrating your activities.
        </p>
    </InfoMessage>
    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-share-alt">Process</Eyebrow>
        </template>
        <template #title>
            <EditableTitle v-model="process" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="process?.identity"
                :entityId="process.identity"
                :entityType="EntityType.PROCESS"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProcessDescription"
            />
        </template>
        <Section class="description" editable>
            <TextAppArtefact
                v-if="process?.identity && description"
                v-model="description"
                :isEditing="isEditing"
            />
        </Section>
        <Section
            class="process-definition"
            editable
            ref="processDefinitionSectionRef"
        >
            <template v-if="process">
                <ProcessDefinition
                    v-if="definition?.content && !isGeneratingBpmn"
                    ref="definitionRef"
                    v-model:bpmnArtefact="definition"
                    v-model:highlightElementId="highlightElementId"
                    :isNavigationEnabled="true"
                    :isScrollingEnabled="false"
                    :isMaximizingEnabled="true"
                    class="main-process-definition"
                >
                    <template #floating-extra>
                        <Button
                            severity="secondary"
                            icon="pi pi-ellipsis-v"
                            size="small"
                            ref="optionsButtonRef"
                            aria-haspopup="true"
                            aria-controls="process_page_popup_menu"
                            aria-label="Diagram Menu"
                            @click="toggleMenu"
                        />
                        <Menu
                            id="process_page_popup_menu"
                            ref="menuRef"
                            :model="contextMenuItems"
                            :popup="true"
                        />
                        <input
                            name="file"
                            type="file"
                            accept=".bpmn"
                            style="display: none"
                            ref="fileUploadRef"
                            @change="
                                (e) =>
                                    uploadProcessDefinition(e as HTMLInputEvent)
                            "
                        />
                    </template>
                </ProcessDefinition>
                <ProcessDefinitionCreator
                    v-else-if="definition"
                    :isEditing="isEditing"
                    v-model="definition"
                    class="main-process-definition"
                />
            </template>
        </Section>
        <Section
            v-if="
                process &&
                (processElements?.length || archivedProcessElements?.length)
            "
            editable
        >
            <Panel class="process-elements-panel" toggleable collapsed>
                <template #header>
                    <Title>
                        Process steps
                        <i
                            v-if="isFetchingProcessElements"
                            class="pi pi-spin pi-spinner process-steps-loading"
                            aria-label="Refreshing process steps"
                        />
                    </Title>
                </template>
                <p class="process-steps-description">
                    Individual tasks, events, gateways, and flows that make up
                    this process.
                </p>
                <ProcessElementTable
                    v-if="process"
                    v-model="highlightElementId"
                    :processElements="processElements"
                />
                <ProcessElementTable
                    class="archived-data-table"
                    v-if="
                        process &&
                        archivedProcessElements &&
                        archivedProcessElements.length > 0
                    "
                    v-model="highlightArchivedElementId"
                    :processElements="archivedProcessElements"
                    header="Archived"
                />
            </Panel>
        </Section>
        <Section editable>
            <Documents
                v-if="process"
                :isEditing="isEditing"
                entityType="process"
                :entity="process"
                :artefacts="artefacts"
            />
        </Section>
    </Page>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />
</template>

<script setup lang="ts">
import Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import ProcessElementTable from "@/components/ProcessElementTable.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Title from "@/components/Title.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useGenerateBpmnDialog } from "@/composables/generate-bpmn-dialog";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useProcessManager } from "@/composables/process-manager";
import { usePageHints } from "@/composables/use-page-hints";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { useTaskManager } from "@/stores/task-store";
import { HTMLInputEvent } from "@/types";
import { downloadBlob } from "@/utils";

const processDefinitionSectionRef = ref<HTMLElement | null>(null);
const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints([
    {
        id: "process-add-definition",
        targetRef: processDefinitionSectionRef,
        title: "Orchestrate your workflow",
        content:
            "Use BPMN to visualize lanes, tasks, and flows — and turn this process into a clear, shared blueprint.",
    },
]);

useTaskManager();
const {
    process,
    isFetching: isFetchingProcess,
    definition,
    description,
    isGeneratingBpmn,
} = useProcessManager();
const { isEditing } = useEditManager();
const { startEditing } = useEditStore();
const { artefacts } = useArtefactsManager();

const router = useRouter();
const route = useRoute();

// Popup menu
const menuRef = ref<InstanceType<typeof Menu>>();
const definitionRef = ref<InstanceType<typeof ProcessDefinition>>();
const fileUploadRef = ref<HTMLInputElement | null>(null);

const toggleMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    menuRef.value?.toggle(event);
};

const isBpmnFile = (files?: FileList | null) => {
    if (!files || files.length !== 1) {
        return false;
    }
    return files[0].name.endsWith(".bpmn");
};

const uploadProcessDefinition = async (event: HTMLInputEvent) => {
    if (!event.target.files || !isBpmnFile(event.target.files)) {
        return;
    }
    await definition.value?.updateContent(event.target.files[0]);
    await definition.value?.persist();
    startEditing();
};

const downloadAsBpmn = () => {
    if (process.value && definition.value) {
        downloadBlob(
            URL.createObjectURL(definition.value.blob),
            `${process.value.name}.bpmn`
        );
    }
};

const downloadAsSvg = async () => {
    if (!definitionRef.value || !process.value) {
        return;
    }
    const svg = await definitionRef.value.getImage("svg");
    if (!svg) {
        return;
    }
    downloadBlob(URL.createObjectURL(svg), `${process.value.name}.svg`);
};

const downloadAsPng = async () => {
    if (!definitionRef.value || !process.value) {
        return;
    }
    const png = await definitionRef.value.getImage("png");
    if (!png) {
        return;
    }
    downloadBlob(URL.createObjectURL(png), `${process.value.name}.png`);
};

const { open: openGenerateDialog } = useGenerateBpmnDialog({
    artefact: definition,
});

const contextMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [
        {
            label: "Download as SVG",
            icon: "pi pi-image",
            command: downloadAsSvg,
        },
        {
            label: "Download as PNG",
            icon: "pi pi-image",
            command: downloadAsPng,
        },
        {
            label: "Download as BPMN",
            icon: "pi pi-download",
            command: downloadAsBpmn,
        },
        {
            label: "Upload a BPMN diagram",
            icon: "pi pi-upload",
            command: () => fileUploadRef.value?.click(),
        },
        {
            label: "Regenerate process",
            icon: "pi pi-sparkles",
            command: () => openGenerateDialog(),
        },
        {
            label: "History",
            icon: "pi pi-history",
            command: () => {
                router.push({
                    name: "processDefinitionPage",
                    params: {
                        ...route.params,
                        version: "current",
                    },
                });
            },
        },
    ];
    return items;
});
const {
    processElements,
    archivedProcessElements,
    isFetching: isFetchingProcessElements,
} = useProcessElementsManager();
const { generateContent } = useGenerateDescriptionDialog();
const isLoading = ref(false);

const displaySpinner = computed(() => {
    return Boolean(isFetchingProcess.value || isLoading.value);
});

const highlightElementId = ref<string | null>();
const highlightArchivedElementId = ref<string | null>();

const generateProcessDescription = async () => {
    if (!process.value) {
        return;
    }
    await generateContent(
        process.value.identity,
        EntityType.PROCESS,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        definition.value?.content ||
        description.value?.content ||
        artefacts.value?.length > 0
    );
});
</script>

<style scoped lang="scss">
.process-definition {
    height: 600px;
    max-height: 50vh;
    display: flex;
    padding: 0;
}

.process-elements-panel {
    --p-panel-toggleable-header-padding: 0;
    border: none;
}

.process-steps-description {
    margin: 0 0 1rem;
    color: var(--p-text-muted-color);
    font-size: 0.875rem;
    font-style: italic;
}

.process-steps-loading {
    margin-left: 0.5rem;
    color: var(--p-text-muted-color);
    font-size: 0.875rem;
}

.description {
    z-index: 3;
}

.archived-data-table {
    margin-top: 1rem;
}
</style>
